import AuthService from "./AuthService";

/**
 * @namespace com.ang.portal.service
 */

export interface UserPluginVariable {
	key: string;
	label?: string;
	description?: string;
	group?: string;
	type?: string;
	required: boolean;
	secret: boolean;
	valueHelp?: {
		path: string;
		title?: string;
		/** Allow picking several entries; stored comma separated. */
		multi?: boolean;
	};
	isSet: boolean;
	value: string;
}

export interface PluginVariableValueHelpOption {
	key: string;
	text: string;
	description?: string;
}

export interface UserPluginVariablesGroup {
	pluginId: string;
	pluginName: string;
	/** Optional "{i18n>key}" label from the plugin manifest; falls back to pluginName. */
	pluginDisplayName?: string;
	variables: UserPluginVariable[];
}

const UserPluginVarsService = {
	async getForUser(userId: number): Promise<UserPluginVariablesGroup[]> {
		const response = await AuthService.authenticatedFetch(
			`/api/users/${encodeURIComponent(String(userId))}/plugin-variables`,
		);
		if (!response.ok) {
			throw new Error("Failed to fetch user plugin variables");
		}
		return response.json() as Promise<UserPluginVariablesGroup[]>;
	},

	async getForUserPlugin(
		userId: number,
		pluginId: string,
	): Promise<UserPluginVariablesGroup> {
		const response = await AuthService.authenticatedFetch(
			`/api/users/${encodeURIComponent(String(userId))}/plugin-variables/${encodeURIComponent(pluginId)}`,
		);
		if (!response.ok) {
			throw new Error("Failed to fetch user plugin variables");
		}
		return response.json() as Promise<UserPluginVariablesGroup>;
	},

	async update(
		userId: number,
		pluginId: string,
		values: Record<string, string | null>,
	): Promise<UserPluginVariablesGroup> {
		const response = await AuthService.authenticatedFetch(
			`/api/users/${encodeURIComponent(String(userId))}/plugin-variables/${encodeURIComponent(pluginId)}`,
			{
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ values }),
			},
		);
		if (!response.ok) {
			const error = (await response
				.json()
				.catch(() => ({ message: "Update failed" }))) as { message: string };
			throw new Error(error.message);
		}
		return response.json() as Promise<UserPluginVariablesGroup>;
	},

	async getValueHelpOptions(
		pluginId: string,
		path: string,
	): Promise<PluginVariableValueHelpOption[]> {
		const routePath = path
			.split("/")
			.filter((segment) => segment.length > 0)
			.map((segment) => encodeURIComponent(segment))
			.join("/");
		const response = await AuthService.authenticatedFetch(
			`/api/plugins/${encodeURIComponent(pluginId)}/${routePath}`,
		);
		if (!response.ok) {
			const error = (await response
				.json()
				.catch(() => ({ message: "Failed to fetch value help options" }))) as {
				message?: string;
			};
			throw new Error(error.message || "Failed to fetch value help options");
		}

		const payload = (await response.json()) as unknown;
		if (!Array.isArray(payload)) {
			return [];
		}

		return payload
			.filter(
				(item): item is Record<string, unknown> =>
					!!item && typeof item === "object",
			)
			.map((item) => ({
				key: typeof item.key === "string" ? item.key : "",
				text:
					typeof item.text === "string"
						? item.text
						: typeof item.key === "string"
							? item.key
							: "",
				description:
					typeof item.description === "string" ? item.description : undefined,
			}))
			.filter((item) => item.key.length > 0);
	},
};

export default UserPluginVarsService;
